import { Injectable } from '@angular/core';
import { ThesaurusEntry } from '@interfaces/thesaurus-entry.interface';
import { ThesaurusPath } from '@interfaces/thesaurus-path.interface';
import { ThesaurusSelectedTerm } from '@interfaces/thesaurus-selected-term.interface';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { error } from 'jquery';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject } from 'rxjs';
import { EventsService } from '../events/events.service';

@Injectable({
  providedIn: 'root'
})

export class TermsService {
    private allCheckedEntryNamesSubject = new BehaviorSubject<ThesaurusSelectedTerm[]>([]);
    allCheckedEntryNames$ = this.allCheckedEntryNamesSubject.asObservable();

    private thesaurusEntriesSubject = new BehaviorSubject<ThesaurusEntry[]>([]);
    thesaurusEntries$ = this.thesaurusEntriesSubject.asObservable();

    private offset = 0;
    private isDisplayAll: boolean = false;
    private isSearchResults: boolean = false;
    private searchTerm: string = '';
    private searchOption: string = '';
    private selectedFacet: string = '';
    private searchCategory: string = '';
    private searchType: string = '';
    private hasSuggestedTerms: boolean = false;
    private isLoading: boolean = true;
    private isRelationalView: boolean = false;

    constructor(
        private thesaurusService: ThesaurusService, private eventsService: EventsService
    ) {

    }

    populateEntriesFacet(thesaurus: Thesaurus, selectedFacet: string, token: string, offset: number) {
        this.clearEntries();
        this.isLoading = true;
        this.eventsService.emitShowIndexSpinner();
        this.thesaurusService.getThesaurusEntriesByFacet(thesaurus, selectedFacet, token, offset).subscribe(
            entries => {
                this.thesaurusEntriesSubject.next(entries);
                this.hasSuggestedTerms = false;
                this.offset = offset;
                this.selectedFacet = selectedFacet;
                this.searchTerm = '';
                this.searchOption = '';
                this.isLoading = false;
                this.eventsService.emitHideIndexSpinner();
            },
            error => {
                this.thesaurusEntriesSubject.next([]);
                this.thesaurusService.setThesaurusServiceError(true);
                this.isLoading = false;
                this.eventsService.emitHideIndexSpinner();
            });
    }

    populateEntriesAll(thesaurus: Thesaurus, token: string, offset: number) {
        this.clearEntries();
        this.isLoading = true;
        this.eventsService.emitShowIndexSpinner();
        this.thesaurusService.getThesaurusEntriesAll(thesaurus, token, offset).subscribe(
            entries => {
                this.thesaurusEntriesSubject.next(entries);
                this.hasSuggestedTerms = false;
                this.offset = offset;
                this.selectedFacet = 'All';
                this.isDisplayAll = true;
                this.isLoading = false;
                this.eventsService.emitHideIndexSpinner();
            },
            error => {
                this.thesaurusEntriesSubject.next([]);
                this.thesaurusService.setThesaurusServiceError(true);
                this.isLoading = false;
                this.eventsService.emitHideIndexSpinner();
            }
        );
    }

    populateEntriesSearchTerm(thesaurus: Thesaurus, searchTerm: string, searchOption: string, token: string, offset: number, searchCategory?: string, searchType?: string) {
        this.clearEntries();
        this.isLoading = true;
        this.eventsService.emitShowIndexSpinner();
        this.thesaurusService.getThesaurusEntriesSearchTerm(thesaurus, searchTerm, searchOption, token, offset, searchCategory, searchType).subscribe(
            searchResults => {
                this.thesaurusEntriesSubject.next(searchResults.thesaurusEntries);
                this.hasSuggestedTerms = searchResults.hasSuggestedTerms;
                this.offset = offset;
                this.isSearchResults = true;
                this.searchTerm = searchTerm;
                this.searchOption = searchOption;
                this.selectedFacet = '';
                this.searchCategory = searchCategory || '';
                this.searchType = searchType || '';
                this.isLoading = false;
                this.eventsService.emitHideIndexSpinner();
            },
            error => {
                this.thesaurusEntriesSubject.next([]);
                this.thesaurusService.setThesaurusServiceError(true);
                this.isLoading = false;
                this.eventsService.emitHideIndexSpinner();
            }
        );
    }

    getEntries() {
        return this.thesaurusEntriesSubject.getValue();
    }

    incrementOffset() {
      this.offset += 50;
    }

    decrementOffset() {
      this.offset -= 50;
    }

    resetOffset() {
      this.offset = 0;
    }

    getOffset() {
        return this.offset;
    }

    getSearchTerm() {
        return this.searchTerm;
    }

    getSearchOption() {
        return this.searchOption;
    }

    getSelectedFacet() {
        return this.selectedFacet;
    }

    getSearchCategory() {
        return this.searchCategory;
    }

    getSearchType() {
        return this.searchType;
    }

    getIsSearchResults() {
        return this.isSearchResults;
    }

    getHasSuggestedTerms() {
        return this.hasSuggestedTerms;
    }

    clearEntries() {
        this.thesaurusEntriesSubject.next([]);
    }

    getIsLoading() {
        return this.isLoading;
    }
    
    getIsRelationalView() {
        return this.isRelationalView;
    }

    setIsRelationalView(value: boolean) {
        this.isRelationalView = value;
    }

    getAllCheckedEntryNames(): ThesaurusSelectedTerm[] {
        return this.allCheckedEntryNamesSubject.getValue();
    }

    /**
     * Populates the allCheckedEntryNames array with a term and its associated properties. Invoked when the main checkbox for a term is clicked (in the thesaurus-detail component), or when a "related term" checkbox is clicked (in the collapsing-notes component).
     *
     * NB: When a term is initially checked by a user (and added to the allCheckedEntryNames array), it will remain in the array (regardless of its "checked" state) until the array is cleared with a call to clearList()
     */
    updateList(name: string, checkboxType: string, checkedState: boolean, notUnique: boolean, paths?: ThesaurusPath[], parent?: ThesaurusEntry) {
        const allCheckedEntryNames = this.allCheckedEntryNamesSubject.getValue();
        //const entryIndex = allCheckedEntryNames.findIndex(item => (item.name === name) && (item.paths && paths? item.paths[0].tn === paths[0].tn : true));
        const entryIndex = this.findEntryIndexByName(name, paths);
        if (entryIndex !== -1) {
            this.updateCheckedState(name, checkboxType, checkedState, paths);
        } else {
            switch(checkboxType) {
                case 'main':
                    allCheckedEntryNames.push({ name, explode: false, major: false, related: false, mainChecked: true, relatedChecked: false, majorChecked: false, explodeChecked: false, qualifiers: [], paths: paths, notUnique: notUnique });
                    break;
                case 'related':
                    allCheckedEntryNames.push({ name, explode: false, major: false, related: true, mainChecked: false, relatedChecked: true, majorChecked: false, explodeChecked: false, qualifiers: [], paths: paths, notUnique: notUnique });
                    break;
                case 'qualifier':
                    this.buildEntryNamesWithQualifiers(name, checkedState, notUnique, parent, paths);
                    break;
                default:
                    console.error('Unknown type:', checkboxType);
            }
        }

        this.allCheckedEntryNamesSubject.next(allCheckedEntryNames);
    }

    buildEntryNamesWithQualifiers(name: string, checkedState: boolean, notUnique: boolean, parent?: ThesaurusEntry, paths?: ThesaurusPath[]) {
        const allCheckedEntryNames = this.allCheckedEntryNamesSubject.getValue();
        const parentName = parent?.name ?? '';
        const parentIndex = this.findEntryIndexByName(parentName, paths);

        if (parentIndex !== -1) {
            // Parent exists, check if qualifier is already present
            const qualifiers = allCheckedEntryNames[parentIndex].qualifiers;
            const qualifierExists = qualifiers.some(q => q.name === name);
            if (!qualifierExists) {
                qualifiers.push({ name: name, parentTerm: parent!, qualifierChecked: checkedState });
            } else {
                this.updateCheckedState(name, "qualifier", checkedState, paths);
            }
        } else {
            // Parent does not exist, add new entry with qualifier
            allCheckedEntryNames.push({ name: parentName, explode: false, major: false, related: false,
                mainChecked: false, relatedChecked: false, majorChecked: false, explodeChecked: false,
                qualifiers: [{ name: name, parentTerm: parent!, qualifierChecked: true }], paths: paths, notUnique: notUnique
            });
        }
    }

    findEntryIndexByName(name: string, paths?: ThesaurusPath[]): number {
        const allCheckedEntryNames = this.allCheckedEntryNamesSubject.getValue();
        for (let i = 0; i < allCheckedEntryNames.length; i++) {
            //Check if the name matches the current entry's name
            if (allCheckedEntryNames[i].name.trim().toUpperCase() === name.trim().toUpperCase()) {
                if (paths && allCheckedEntryNames[i].paths) {
                    //If paths are provided, check if they match as well
                    const entryPathsStr = allCheckedEntryNames[i].paths?.[0]?.tn;
                    const searchPathsStr = paths?.[0]?.tn;
                    if (entryPathsStr && searchPathsStr) {
                        if (entryPathsStr === searchPathsStr) {
                            return i;
                        }
                    } else if (this.comparePathTerms(allCheckedEntryNames[i].paths?.[0], paths?.[0])) {
                        return i;
                    }
                } else if (!paths && !allCheckedEntryNames[i].paths) {
                    return i;
                }
            } else if (allCheckedEntryNames[i].qualifiers && allCheckedEntryNames[i].qualifiers.length > 0) {
                //Check within qualifiers
                const qualifierIndex = allCheckedEntryNames[i].qualifiers.findIndex(q => q.name.trim().toUpperCase() === name.trim().toUpperCase());
                if (qualifierIndex !== -1) {
                    return i;
                }
            }
        }
        return -1;
    }

    /**
     * To check or uncheck individual checkboxes after a "term/entry" has initially been selected.
     *
     * NB: When a term is initially checked by a user (and added to the allCheckedEntryNames array), it will remain in the array (regardless of its "checked" state) until the array is cleared with a call to clearList().
     */
    updateCheckedState(name: string, checkboxType: string, checkedState: boolean, paths?: ThesaurusPath[], qualIndex?: number, parentName?: string) {
         const allCheckedEntryNames = this.allCheckedEntryNamesSubject.getValue();
         const entryIndex = this.findEntryIndexByName(name, paths);

        //Check if a term is in the allCheckedEntryNames array
        //Switch cases below are determined by the checkboxType
        if (entryIndex !== -1) {
            switch(checkboxType) {
                case 'main':
                    allCheckedEntryNames[entryIndex].mainChecked = checkedState;
                    allCheckedEntryNames[entryIndex].relatedChecked = checkedState;
                    break;
                case 'major':
                    allCheckedEntryNames[entryIndex].major = checkedState;
                    allCheckedEntryNames[entryIndex].majorChecked = checkedState;
                    break;
                case 'explode':
                    allCheckedEntryNames[entryIndex].explode = checkedState;
                    allCheckedEntryNames[entryIndex].explodeChecked = checkedState;
                    break;
                case 'related':
                    allCheckedEntryNames[entryIndex].relatedChecked = checkedState;
                    allCheckedEntryNames[entryIndex].mainChecked = checkedState;
                    break;
                case 'qualifier':
                    const parentIndex = allCheckedEntryNames.findIndex(item => item.name == parentName && item.qualifiers?.filter(q => q.name === name));
                    if (qualIndex !== undefined && allCheckedEntryNames[parentIndex].qualifiers[qualIndex]) {
                        allCheckedEntryNames[parentIndex].qualifiers[qualIndex].qualifierChecked = checkedState;
                    }
                    break;
                default:
                    console.error('Unknown type:', checkboxType);
            }
        }

        this.allCheckedEntryNamesSubject.next(allCheckedEntryNames);
    }

    //Remove all items from the array
    clearList() {
        this.allCheckedEntryNamesSubject.next([]);
    }

    getTerm(): ThesaurusSelectedTerm[] {
        return this.allCheckedEntryNamesSubject.getValue().filter(item => item.mainChecked);
    }

    //Term count is based on whether items in the array have a "checked" property value of true
    getTermCount(): number {
        const list = this.allCheckedEntryNamesSubject.getValue();

        const mainOrRelatedCount = list.filter(item => item.mainChecked || item.relatedChecked).length;

        const qualifierCheckedCount = list.reduce((sum, item) => {
            if (item.qualifiers && item.qualifiers.length > 0) {
                return sum + item.qualifiers.filter(q => q.qualifierChecked).length;
            }
            return sum;
        }, 0);

        return mainOrRelatedCount + qualifierCheckedCount;
    }

    comparePaths(path1: ThesaurusPath | undefined, path2: ThesaurusPath[] | undefined): boolean {
        if (!path1 || !path2) {
            return false;
        }
        let matchFound = false;
        for (let p of path2) {
            if (path1.terms.length !== p.terms.length) {
                continue;
            }
            if (path1.tn && p.tn ) {
                if (path1.tn === p.tn)
                    return true;
            } else {
                let currentMatch = path1.terms.every((value, index) => value.termOrdinal === p.terms[index].termOrdinal);
                if (path2.length === 1)
                    return currentMatch;
                else
                    matchFound = matchFound || currentMatch;
            }
        }
        return matchFound;
    }

    isChecked(name: string, paths?: ThesaurusPath[]): boolean {
        const isChecked = this.allCheckedEntryNamesSubject.getValue().some(item => item.name === name && (paths ? this.comparePaths(item.paths?.[0], paths) : true) && item.mainChecked);
        return isChecked;
    }

    isExplodeChecked(name: string, paths?: ThesaurusPath[]): boolean {
        const isChecked = this.allCheckedEntryNamesSubject.getValue().some(item => item.name === name && (paths ? this.comparePaths(item.paths?.[0], paths) : true) && item.explodeChecked);
        return isChecked;
    }

    isMajorChecked(name: string, paths?: ThesaurusPath[]): boolean {
        const isChecked = this.allCheckedEntryNamesSubject.getValue().some(item => item.name === name && (paths ? this.comparePaths(item.paths?.[0], paths) : true) && item.majorChecked);
        return isChecked;
    }

    isRelatedChecked(name: string): boolean {
        const isChecked = this.allCheckedEntryNamesSubject.getValue().some(item => item.name === name && item.relatedChecked);
        return isChecked;
    }

    isRelatedTerm(name: string): boolean {
        const isRelated = this.allCheckedEntryNamesSubject.getValue().some(item => item.name === name && item.related);
        return isRelated;
    }

    //To check if the allCheckedEntryNamesSubject list contains any qualifier terms
    hasQualifiers(entry: string): boolean {
        return this.allCheckedEntryNamesSubject.getValue().some((item, index) => {
            return item.name == entry && item.qualifiers.length > 0;
        });
    }

    //To check if any of the qualifiers associated with a parentTerm have been checked.
    isQualifierTermChecked(name: string, parentName: string): boolean {
      return this.allCheckedEntryNamesSubject.getValue().some(item => item.name == parentName && 
        (item.qualifiers?.some(q => q.name === name && q.qualifierChecked))
      );
    }

    //Removes parentheses and braces. Replaces whitespace with hyphens, then transforms the final string to lowercase.
    sanitizeName(name: string): string {
        return name.replace(/[()]/g, '').replace(/\s+/g, '-').toLowerCase();
    }

    comparePathTerms(path1: ThesaurusPath | undefined, path2: ThesaurusPath | undefined): boolean {
        if (!path1 || !path2) {
            return false;
        }
        if (path1.terms.length !== path2.terms.length) {
            return false;
        }
        for (let i = 0; i < path1.terms.length; i++) {
            if (path1.terms[i].termOrdinal !== path2.terms[i].termOrdinal) {
                return false;
            }
        }
        return true;
    }

}
